import { getCategoryAccountManagers } from '../util/api';
import { storableError } from '../util/errors';

// Which account manager owns which listing category.
//
// This feeds the "Have a requirement? Here to help" card on the search page and
// the "Listing managed by" card in the listing page's right rail. Both need the
// same answer — given a Sharetribe category id, who is the manager — so the map
// lives here rather than in either page's own duck.
//
// WHY THE WHOLE MAP AND NOT ONE MANAGER
//   The search page card has to change the instant somebody picks a different
//   category, with no page load. Fetching per category would put a request in
//   the way of that, and would refetch the same handful of rows all day. The
//   map is small, so it comes down once and the card reads from it.
//
// The fetch runs in loadData, so it also runs while the page is rendered on the
// server and the card is in the HTML rather than appearing a moment later. See
// getUniversal in src/util/api.js for how one call is made to work in Node as
// well as in the browser.
//
// A CATEGORY MISSING FROM THE MAP IS NORMAL. Only some categories have a desk.
// Every reader leaves its card out rather than drawing a placeholder, so there
// is no loading or error state to render here either: an empty map and a failed
// fetch look the same on screen, which is what we want for a card that must
// never be the reason a listing fails to render.

// ================ Action types ================ //

export const FETCH_CATEGORY_MANAGERS_REQUEST =
  'app/categoryAccountManagers/FETCH_CATEGORY_MANAGERS_REQUEST';
export const FETCH_CATEGORY_MANAGERS_SUCCESS =
  'app/categoryAccountManagers/FETCH_CATEGORY_MANAGERS_SUCCESS';
export const FETCH_CATEGORY_MANAGERS_ERROR =
  'app/categoryAccountManagers/FETCH_CATEGORY_MANAGERS_ERROR';

// ================ Reducer ================ //

const initialState = {
  managersByCategory: {},
  fetchInProgress: false,
  fetchError: null,
};

export default function categoryAccountManagersReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_CATEGORY_MANAGERS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_CATEGORY_MANAGERS_SUCCESS:
      return { ...state, managersByCategory: payload, fetchInProgress: false };
    case FETCH_CATEGORY_MANAGERS_ERROR:
      // The map is left as it was. On a first load that is empty and no card is
      // drawn; on a later page the managers already held are better than
      // suddenly dropping a card the reader was looking at.
      return { ...state, fetchError: payload, fetchInProgress: false };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchCategoryManagersRequest = () => ({ type: FETCH_CATEGORY_MANAGERS_REQUEST });

export const fetchCategoryManagersSuccess = managersByCategory => ({
  type: FETCH_CATEGORY_MANAGERS_SUCCESS,
  payload: managersByCategory,
});

export const fetchCategoryManagersError = e => ({
  type: FETCH_CATEGORY_MANAGERS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCategoryAccountManagers = () => dispatch => {
  dispatch(fetchCategoryManagersRequest());

  return getCategoryAccountManagers()
    .then(response => {
      dispatch(fetchCategoryManagersSuccess(response.managersByCategory || {}));
      return response.managersByCategory;
    })
    .catch(e => {
      dispatch(fetchCategoryManagersError(storableError(e)));
    });
};

// ================ Selectors ================ //

/**
 * The manager who owns a category, or null.
 *
 * The map's keys are normalised on the server — trimmed and lower-cased — so
 * the id looked up here has to be normalised the same way. Console spells one
 * category "Art" while server/cron/helpers/approveListingsFunctions.js spells
 * it "art"; without this, one of those two would silently find nobody.
 */
export const managerForCategory = (managersByCategory, categoryId) => {
  if (!managersByCategory || !categoryId) {
    return null;
  }

  return managersByCategory[String(categoryId).trim().toLowerCase()] || null;
};
